/* eslint-disable @typescript-eslint/typedef */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { rxActions } from '@rx-angular/state/actions';
import { SharedService } from '@shared/shared.service';
import { SharedState } from '@shared/shared.state';
import { VesselItinerary } from '@shared/types/shared.types';
import { catchError, exhaustMap, filter, of, tap } from 'rxjs';

interface ISharedAction {
  getVesselItineraryById: {
    vesselId: any;
    queryParams?: Params;
  };
  getVesselItineraryByIdSuccess: {
    pagination: any;
    itinerary: VesselItinerary[];
  };
  getCurrenciesByName: {
    name: string;
  };
  openDrawer: {
    isOpen: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class SharedActions {
  actions = rxActions<ISharedAction>();

  private readonly _sharedService = inject(SharedService);
  private readonly _sharedState = inject(SharedState);

  constructor() {
    this._subscribeToGetVesselItinerary$();
    this._subscribeToGetVesselItineraryByIdSuccess$();
    this._subscribeToGetCurrenciesByName$();
    this._subscribeToOpenDrawer$();
  }

  private _subscribeToGetVesselItinerary$(): void {
    type APIResponse = UnwrapReturnObs<typeof this._sharedService.getVesselItineraryById>;

    this.actions.onGetVesselItineraryById(
      (payload$) =>
        payload$.pipe(
          exhaustMap((payload) =>
            this._sharedService.getVesselItineraryById(payload.vesselId, payload?.queryParams).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        ),
      ({ pagination, data }) => {
        this.actions.getVesselItineraryByIdSuccess({ pagination, itinerary: data });
      }
    );
  }

  private _subscribeToGetVesselItineraryByIdSuccess$(): void {
    this.actions.getVesselItineraryByIdSuccess$.subscribe({
      next: ({ pagination, itinerary }) => {
        this._sharedState.set({
          vesselItineraryPagination: pagination,
          vesselItinerary: itinerary
        });
      }
    });
  }

  private _subscribeToGetCurrenciesByName$(): void {
    type APIResponse = UnwrapReturnObs<typeof this._sharedService.searchCurrenciesByName>;

    this.actions.onGetCurrenciesByName(
      (payload$) =>
        payload$.pipe(
          exhaustMap((payload) =>
            this._sharedService.searchCurrenciesByName(payload.name).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        ),
      (searchedCurrencies) => {
        this._sharedState.set({ searchedCurrencies });
      }
    );
  }

  private _subscribeToOpenDrawer$(): void {
    this.actions.onOpenDrawer(
      (payload$) => payload$.pipe(tap((isOpen) => console.log('Drawer state change:', isOpen))),
      (isOpen: boolean) => {
        this._sharedState.set({ isDrawerOpen: isOpen });
      }
    );
  }
}
